import { useState } from "react";
import { X, GraduationCap, Flame, BarChart2, KeyRound, Sparkles, Trophy, MessagesSquare } from "lucide-react";

/**
 * Player-facing "Grow Your Knowledge" guide.
 *
 * iter87.180 Item C + D — Restructure:
 *   • Standalone SMACK STREAM tab removed. Smack lives inside the
 *     DOMINATE sub-section of the Your Dashboard tab now.
 *   • Your Dashboard tab reorganized into DRAFT / CHEER / DOMINATE
 *     sub-sections so the guide mirrors the on-page bento grouping.
 *   • Signing back in — final locked copy (four paths + footer,
 *     Invite Code IS required for path 1).
 */
const TOPICS = [
    { key: "welcome",  label: "Welcome",         icon: Sparkles },
    { key: "dash",     label: "Your Dashboard",  icon: BarChart2 },
    // iter87.199.5 — "Rivalry Points" tab retired with the Crown Race
    // UI deprecation. Backend tally still runs silently; when we
    // re-surface it, restore this entry.
    { key: "recover",  label: "Signing back in", icon: KeyRound },
];

export default function UserGuideModal({ open, onClose, tournamentCount = 1 }) {
    // iter87.199.5 — `tournamentCount` kept in the signature for
    // signature compatibility with LeagueDashboard callers; unused
    // now that the multi-tournament Crown Race tab is deprecated.
    void tournamentCount;
    const [topic, setTopic] = useState("welcome");
    if (!open) return null;
    const visibleTopics = TOPICS;

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80"
            data-testid="user-guide-backdrop"
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div
                className="brutal-card max-w-3xl w-full max-h-[88dvh] flex flex-col"
                data-testid="user-guide-modal"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between gap-3 p-5 border-b-2 border-[#222]">
                    <div className="flex items-center gap-2 min-w-0">
                        <GraduationCap className="w-5 h-5 text-cyber shrink-0" />
                        <h2 className="font-display font-black text-xl uppercase truncate">
                            Dominate Your Tournaments
                        </h2>
                    </div>
                    <button
                        onClick={onClose}
                        className="brutal-btn-ghost p-1.5"
                        data-testid="user-guide-close-btn"
                        aria-label="Close guide"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>

                {/* Topic chips */}
                <div className="px-5 pt-4 pb-2 flex gap-1.5 flex-wrap border-b-2 border-[#222]">
                    {visibleTopics.map((t) => {
                        const Icon = t.icon;
                        const active = t.key === topic;
                        return (
                            <button
                                key={t.key}
                                data-testid={`user-guide-tab-${t.key}-btn`}
                                onClick={() => setTopic(t.key)}
                                className={`brutal-btn text-[10px] py-1.5 px-2.5 ${
                                    active ? "bg-cyber text-black" : "bg-transparent text-zinc-300 border-[#333] shadow-none"
                                }`}
                            >
                                <Icon className="w-3 h-3" /> {t.label}
                            </button>
                        );
                    })}
                </div>

                {/* Body */}
                <div className="overflow-y-auto px-5 py-4 flex-1 text-sm text-zinc-300 leading-relaxed">
                    {topic === "welcome" && <WelcomeBlock />}
                    {topic === "dash" && <DashboardBlock />}
                    {topic === "recover" && <RecoveryBlock />}
                </div>

                <div className="p-3 border-t-2 border-[#222] flex justify-end bg-ink">
                    <button
                        type="button"
                        onClick={onClose}
                        className="brutal-btn-primary text-xs py-2"
                        data-testid="user-guide-done-btn"
                    >
                        Got it
                    </button>
                </div>
            </div>
        </div>
    );
}

/* --------------------- Sections --------------------- */

function Section({ icon: Icon, title, children }) {
    return (
        <div className="space-y-3">
            <div className="flex items-center gap-2">
                <Icon className="w-5 h-5 text-cyber" />
                <h3 className="font-display font-black text-lg uppercase tracking-tight">{title}</h3>
            </div>
            {children}
        </div>
    );
}

function SubSection({ icon: Icon, title, testId, children }) {
    return (
        <div className="space-y-2" data-testid={testId}>
            <div className="flex items-center gap-2">
                <Icon className="w-4 h-4 text-cyber" />
                <h4 className="font-display font-black text-sm uppercase tracking-[0.14em] text-white">
                    {title}
                </h4>
            </div>
            <div className="pl-1 space-y-2">{children}</div>
        </div>
    );
}

function Bullets({ items }) {
    return (
        <ul className="list-disc list-inside space-y-1.5 text-xs text-zinc-300">
            {items.map((b, i) => <li key={i}>{b}</li>)}
        </ul>
    );
}

function WelcomeBlock() {
    return (
        <Section icon={Sparkles} title="Welcome to your Tiny Crew Rivalry!">
            <p>
                You're here because someone in your crew talked you into a friendly pick'em. Here's the gist: your <strong className="text-white">Tiny Crew Rivalry</strong> can run <strong className="text-white">one or many Tournaments</strong> (World Cup, March Madness, golf majors, custom format — whatever your admin sets up). For each one, you get assigned (or pick) a handful of teams/players, and they rack up points as the Tournament unfolds. The better your squad performs, the higher you climb on that Tournament's leaderboard.
            </p>
        </Section>
    );
}

function DashboardBlock() {
    return (
        <Section icon={BarChart2} title="Your dashboard at a glance">
            <p>
                The dashboard is where you'll spend most of your time. It's organised into three rhythms — <strong className="text-white">Draft</strong>, <strong className="text-white">Cheer</strong>, and <strong className="text-white">Dominate</strong> — so you always know what's next.
            </p>

            <SubSection icon={Trophy} title="Draft" testId="user-guide-dash-draft">
                <p className="text-xs text-zinc-300">
                    Everything you do before games start lives here. Depending on the tournament format your admin picked, this can include:
                </p>
                <Bullets items={[
                    "Rank your teams / players so the auto-draft knows what you want most.",
                    "Choose your Champion — the team you think will lift the trophy.",
                    "Make your Pre-season or weekly picks (season pot pick'em, matchday boost, gameday picks).",
                    "Set your Power Pick — the team whose points get multiplied.",
                ]} />
            </SubSection>

            <SubSection icon={BarChart2} title="Cheer" testId="user-guide-dash-cheer">
                <p className="text-xs text-zinc-300">
                    Once your picks are locked and the games kick off, the Cheer tiles are where you track how your crew is doing. Up to four surfaces slice the action different ways depending on format:
                </p>
                <Bullets items={[
                    "🏆 Leaderboard — who's winning this Tournament, plus your stats and picks. Tap the info icon in the header for the exact scoring rules for this tournament.",
                    "📊 Standings — group tables or live field standings (format-dependent).",
                    "🌳 Bracket — knockout visualization once the bracket phase begins (bracket formats only).",
                    "📅 Schedule — past, live, and upcoming games (fixture-based formats only).",
                ]} />
            </SubSection>

            <SubSection icon={MessagesSquare} title="Dominate" testId="user-guide-dash-dominate">
                <p className="text-xs text-zinc-300">
                    The social heart of your Rivalry — talk trash, celebrate, or read your admin's updates.
                </p>
                <Bullets items={[
                    "Smack Stream — every Rivalry has a built-in trash-talk wall. 128 characters per post, 5 posts per 30 minutes so nobody can spam.",
                    "Delete your own posts anytime; your Rivalry admin can delete anyone's if things cross a line.",
                    "Rivalry Messages & Updates — pinned announcements from your admin (new tournament, draft time, prizes, whatever).",
                    "Be ruthless but not mean. Your admin can hide Smack entirely if it gets ugly — don't be that person.",
                ]} />
            </SubSection>
        </Section>
    );
}

// iter87.199.5 — CrownPointsBlock retired with the Crown Race UI
// deprecation. Removed entirely (backend tally still runs silently);
// restore this block if the surface is ever re-enabled.

function RecoveryBlock() {
    return (
        <Section icon={KeyRound} title="Signing back in">
            <p>
                Locked out or on a new device? You have four ways back in. Your <strong className="text-white">personal code is Rivalry-wide</strong> — one code unlocks every Tournament in your Rivalry, current and future.
            </p>
            <Bullets items={[
                <><strong className="text-white">Invite Code + Personal Code + Username</strong> — the primary path. The Invite Code IS required on the sign-in form; pair it with your username and personal code to get straight back in.</>,
                <><strong className="text-white">Landing-page shortcut</strong> — if you still have cookies from a previous session, tap your Rivalry name in the top-right of the landing page to jump back in with one click.</>,
                <><strong className="text-white">Forgot username?</strong> — the sign-in form emails you a magic link (single-use, expires after 7 days) that signs you straight in.</>,
                <><strong className="text-white">Your Rivalry admin</strong> — they can reset your username, change your email on file, or regenerate your personal code for you.</>,
            ]} />
            <p className="text-xs text-zinc-400 pt-1" data-testid="user-guide-recovery-footer">
                Personal codes and invite codes don't expire. Magic links expire after 7 days and are single-use. Save your personal code somewhere safe!
            </p>
        </Section>
    );
}
